/**
 * useHomepageState
 *
 * Patient-scoped JSONB blob for client-defined homepage UI state.
 * Replaces AsyncStorage for plan-step completions, task sections, etc.
 *
 * The backend treats the JSONB as opaque (validated as `Record<string, unknown>`);
 * consumers should narrow specific keys with the accessors below.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";

export type TaskSectionsState = {
  completed: Record<string, boolean>;
  expanded: Record<string, boolean>;
};

/** Conceptual shape of homepage_state — for FE narrowing, not enforced by the API. */
export type HomepageStateShape = {
  completedPlanSteps?: string[];
  taskSections?: TaskSectionsState | null;
  dismissedCards?: Record<string, boolean> | null;
};

const queryKey = (patientId?: string) => ["homepage-state", patientId] as const;

export function useHomepageState() {
  const { client, tenantId, patientId } = useApiClient();
  const qc = useQueryClient();
  const key = queryKey(patientId);

  const query = useQuery({
    queryKey: key,
    enabled: !!tenantId && !!patientId,
    meta: { persist: true },
    queryFn: async () => {
      if (!tenantId || !patientId) return {};
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/homepage-state",
        { params: { path: { tenantId, patientId } } },
      );
      if (error) {
        console.error("[useHomepageState] GET error:", error);
        throwApiError(
          error,
          "Failed to load homepage state",
          "useHomepageState",
          response?.status,
        );
      }
      return data?.data ?? {};
    },
  });

  function set(patch: Partial<HomepageStateShape>) {
    qc.setQueryData<Record<string, unknown>>(key, (current) => ({
      ...(current ?? {}),
      ...patch,
    }));
  }

  return { ...query, set };
}

/**
 * Shallow per-top-level-key merge on the server (`stored || incoming`).
 * Concurrent writes to different keys don't clobber each other.
 */
export function useMergeHomepageState() {
  const { client, tenantId, patientId } = useApiClient();
  const qc = useQueryClient();
  const key = queryKey(patientId);

  return useMutation({
    mutationFn: async (patch: HomepageStateShape) => {
      if (!tenantId || !patientId) return {};
      const { data, error, response } = await client.PATCH(
        "/{tenantId}/patients/{patientId}/homepage-state",
        {
          params: { path: { tenantId, patientId } },
          body: patch,
        },
      );
      if (error) {
        console.error("[useMergeHomepageState] PATCH error:", error);
        throwApiError(
          error,
          "Failed to update homepage state",
          "useMergeHomepageState",
          response?.status,
        );
      }
      return data?.data ?? {};
    },
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Record<string, unknown>>(key);
      qc.setQueryData<Record<string, unknown>>(key, (current) => ({
        ...(current ?? {}),
        ...patch,
      }));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        qc.setQueryData(key, context.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

// ─── Typed accessors (narrowing at the boundary) ───

export function getCompletedPlanSteps(
  state: Record<string, unknown> | undefined,
): string[] {
  const v = state?.completedPlanSteps;
  return Array.isArray(v)
    ? v.filter((x): x is string => typeof x === "string")
    : [];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const EMPTY_TASK_SECTIONS: TaskSectionsState = { completed: {}, expanded: {} };

export function getTaskSections(
  state: Record<string, unknown> | undefined,
): TaskSectionsState {
  const v = state?.taskSections;
  if (!isRecord(v)) return EMPTY_TASK_SECTIONS;
  const completed = isRecord(v.completed) ? v.completed : {};
  const expanded = isRecord(v.expanded) ? v.expanded : {};
  return {
    completed: completed as Record<string, boolean>,
    expanded: expanded as Record<string, boolean>,
  };
}

const EMPTY_DISMISSED_CARDS: Record<string, boolean> = {};

export function getDismissedCards(
  state: Record<string, unknown> | undefined,
): Record<string, boolean> {
  const v = state?.dismissedCards;
  return isRecord(v) ? (v as Record<string, boolean>) : EMPTY_DISMISSED_CARDS;
}
